import { useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Provider } from "rujira.ui";
import {
  REDACTED_BACKEND_URL,
  REDACTED_PRIVACY_CHAIN_ID,
} from "./config";
import { generateLocalProof } from "./clientProof";
import { useRedacted } from "../services/redacted";

import { isV2Enabled } from "./v2/config";

/** @deprecated Private balances now come from the unified Rujira Account API. */
export interface RedactedBalanceType {
  amount: string;
  denom: string;
}

export interface RedactedEncStealthType {
  stealth: string;
  t: string;
}

export interface GasTankBalance {
  amount: string;
}

export const useGasTankBalance = (
  publicAddress: string | null
): GasTankBalance | null => {
  const { data } = useQuery({
    queryKey: ["gas-tank-balance", publicAddress],
    queryFn: async () => {
      if (!publicAddress) return null;
      try {
        const res = await axios.get(`${REDACTED_BACKEND_URL}/gasTank`, {
          params: {
            publicAddress,
          },
        });
        if (res.data && res.data.ok) {
          return { amount: res.data.amount } as GasTankBalance;
        } else {
          if (res.data) console.error(res.data.error);
        }
        return null;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    refetchInterval: publicAddress ? 10000 : false,
  });
  return data || null;
};

export const useGetEncStealth = (
  stealth: string | null
): RedactedEncStealthType | null => {
  const safeStealth = isV2Enabled() ? null : stealth;
  const { data } = useQuery({
    queryKey: ["get-enc-stealth", safeStealth],
    queryFn: async () => {
      if (!safeStealth) return null;
      try {
        const res = await axios.get(`${REDACTED_BACKEND_URL}/getEncStealth`, {
          params: {
            stealth: safeStealth,
          },
        });
        if (res.data && res.data.ok) {
          return {
            stealth: res.data.stealth,
            t: res.data.t,
          } as RedactedEncStealthType;
        } else {
          if (res.data) console.error(res.data.error);
        }
        return null;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    refetchInterval: 1000,
  });
  return data || null;
};

// CHANGED: Accept provider parameter instead of hardcoding "Keplr"
export const useRedactedProof = (provider?: Provider.Key) => {
  const { publicAddress, stealth, encryptionSignature } = useRedacted();
  const encryptionSignatureRef = useRef<string | null>(encryptionSignature);

  useEffect(() => {
    encryptionSignatureRef.current = encryptionSignature;
  }, [encryptionSignature]);

  const getNonce = useCallback(
    async (stealthParam?: string) => {
      const stealthKey = stealthParam || stealth;
      try {
        if (!stealthKey) return null;
        const resNonce = await axios.get(
          `${REDACTED_BACKEND_URL}/getNonce?stealth=${stealthKey}`
        );
        if (resNonce.data) {
          return resNonce.data.ok ? resNonce.data.nonce : "0";
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    [stealth]
  );

  const generateProofWithParams = useCallback(
    async (
      param: string,
      noGenerateProof?: boolean,
      addressParam?: string,
      stealthParam?: string
    ) => {
      const address = addressParam || publicAddress;
      const stealthKey = stealthParam || stealth;
      try {
        if (!address || !stealthKey || !param) {
          throw new Error(
            "Private Mode is not fully unlocked. Unlock it with your wallet and try again."
          );
        }

        const message = `${address.toLowerCase()}-${param.toString()}`;
        // CHANGED: Use connected provider instead of hardcoded "Keplr"
        const walletProvider = provider || "Keplr";
        const signer = Provider.signer(walletProvider);
        if (signer.messageSigningScheme === "eip191") {
          throw new Error(
            `${walletProvider} cannot sign legacy private actions.`
          );
        }
        if (!signer.signMessage) {
          throw new Error(`${walletProvider} cannot sign this private action.`);
        }
        const encryptionSignatureValue = encryptionSignatureRef.current;
        if (!encryptionSignatureValue) {
          throw new Error(
            "Private Mode is locked. Unlock it with your wallet and try again."
          );
        }

        const signature = await signer.signMessage(
          REDACTED_PRIVACY_CHAIN_ID,
          address,
          message
        );
        if (!signature) {
          throw new Error("The wallet did not sign the private action.");
        }
        if (noGenerateProof) {
          return {
            proof: null,
            signature: signature,
          };
        }

        const proof = await generateLocalProof({
          address,
          derivationSignature: encryptionSignatureValue,
          stealth: stealthKey,
          param,
        });
        return {
          proof,
          signature,
        };
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error("Private proof generation failed.");
      }
    },
    [publicAddress, stealth, provider]
  );

  const generateProofMain = useCallback(
    async (
      param: string,
      noGenerateProof?: boolean,
      addressParam?: string,
      stealthParam?: string
    ) => {
      return generateProofWithParams(
        param,
        noGenerateProof,
        addressParam,
        stealthParam
      );
    },
    [generateProofWithParams]
  );

  return {
    getNonce,
    generateProofMain,
    generateProofWithParams,
  };
};
